// Carrusel de onboarding del paciente — 2 pantallas con copy condensado
// del doc `docs/decisions/setup-guide-paciente.md`. Aparece como modal
// full-screen la primera vez que el paciente abre SesionCamara, y queda
// re-accesible vía ícono permanente (HelpCircle) en el pre-session gate.
//
// Refactor del 6/5/2026 redujo de 7 a 2 slides. Razón: feedback post-QA,
// la intro de 7 slides era muy larga y demoraba al paciente en arrancar.
// Slides originales 1 (Distancia), 3 (Ropa), 4 (Fondo), 5 (Iluminación)
// y 6 (Ghost zones) condensados en 4 bullets del slide 0 actual.
// Slide original 2 (Posición + mockup v2) conservado intacto como slide
// 1 final — el visual ancla el cierre antes del botón "Empezar mi sesión".
// Slide original 7 ("Ya está, podés empezar") eliminado — el "empezar"
// queda como label del botón final. Ver `docs/decisions/setup-guide-paciente.md`
// sección "Update 6/5/2026".
//
// Persistencia del flag "ya visto": localStorage key sin bump
// (kinetiq_setup_guide_visto_v1). El refactor no invalida el flag
// conceptualmente — un paciente que ya vio las 7 slides no necesita
// volver a ver las 2 nuevas. Cuando llegue auth real (Sprint 2),
// evaluar mover a columna `setup_guide_visto` en tabla `pacientes` para
// sincronización cross-device.

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "../ui/carousel";
import slide2Img from "@/assets/setup-guide/slide-02-posicion.png";

const STORAGE_KEY = "kinetiq_setup_guide_visto_v1";

export function hasSeenSetupGuide(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function markSetupGuideAsSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // localStorage puede fallar en modo privado de algunos browsers o si
    // el cuota está llena. Falla silenciosa: el carrusel volverá a aparecer
    // la próxima vez, ruido aceptable para MVP.
  }
}

interface SlideData {
  titulo: string;
  body: string;
  /** Emoji fallback. Siempre presente — si `image` falla al cargar
   *  (404 / red), el emoji queda como respaldo silencioso vía onError. */
  illustration: string;
  /** PNG/SVG importado del bundle, opcional. Cuando está, reemplaza
   *  al emoji en el render. */
  image?: string;
  /** Alt descriptivo si hay `image`. Si falta, usa `titulo` como alt. */
  imageAlt?: string;
  /** Lista de puntos clave debajo del body. Si está presente, se
   *  renderiza como UL después del párrafo. Usado para condensar
   *  varios slides clínicos en uno (ej. slide 0 "Antes de empezar"
   *  del refactor del 6/5/2026). */
  bullets?: string[];
}

const SLIDES: SlideData[] = [
  {
    titulo: "Antes de empezar",
    body: "Acomodate así la cámara te capta bien:",
    illustration: "📋",
    bullets: [
      "Pará a 1,5-2 metros con cuerpo entero visible.",
      "Buena luz adelante tuyo (no atrás).",
      "Ropa que contraste con el fondo.",
      "Si un movimiento no se cuenta, no es tu culpa — avisale al kine.",
    ],
  },
  {
    titulo: "Tu posición",
    body: "No te pongas de frente puro a la cámara. Posicionate 3/4 — el lado del cuerpo donde hacés el ejercicio levemente hacia atrás. Eso permite que la cámara vea bien el brazo y el codo del ejercicio. Si te ponés de frente, el codo queda en perfil hacia la cámara y se pierde la detección.",
    illustration: "👤",
    image: slide2Img,
    imageAlt:
      "Comparación entre posición frente a la cámara (incorrecta) y posición 3/4 (correcta), con vistas de lo que ve la cámara en cada caso.",
  },
];

export interface SetupGuideCarruselProps {
  open: boolean;
  onClose: () => void;
}

export function SetupGuideCarrusel({ open, onClose }: SetupGuideCarruselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrentIndex(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  // Reset al primer slide cada vez que el modal se reabre, para que el
  // re-acceso desde el ícono permanente arranque desde 0 y no donde quedó.
  useEffect(() => {
    if (open && api) api.scrollTo(0, true);
  }, [open, api]);

  // ESC cierra (mismo patrón que el drawer mobile del KineSidebar).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const isLast = currentIndex === SLIDES.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="setup-guide-titulo"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 rounded-full p-1.5 bg-background/80 border border-border hover:bg-muted transition-colors"
          aria-label="Cerrar guía"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex-1 min-h-0">
          <Carousel setApi={setApi} opts={{ loop: false, align: "start" }}>
            <CarouselContent>
              {SLIDES.map((slide, i) => (
                <CarouselItem key={i}>
                  <div className="flex flex-col items-center gap-4 px-8 pt-8 pb-6 text-center">
                    {slide.image ? (
                      <img
                        src={slide.image}
                        alt={slide.imageAlt ?? slide.titulo}
                        className="w-full max-w-xs sm:max-w-sm mx-auto rounded-lg"
                      />
                    ) : (
                      <div className="text-7xl mb-2 mt-4" aria-hidden="true">
                        {slide.illustration}
                      </div>
                    )}
                    <h2
                      id={i === 0 ? "setup-guide-titulo" : undefined}
                      className="text-2xl font-semibold"
                    >
                      {slide.titulo}
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {slide.body}
                    </p>
                    {slide.bullets && (
                      <ul className="text-sm text-muted-foreground leading-relaxed text-left space-y-2 mt-1 w-full">
                        {slide.bullets.map((b, j) => (
                          <li key={j} className="flex gap-2">
                            <span aria-hidden="true" className="text-primary">•</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        <div className="flex justify-center gap-2 py-2 px-4">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentIndex ? "w-6 bg-primary" : "w-2 bg-muted"
              }`}
              aria-label={`Ir a la pantalla ${i + 1} de ${SLIDES.length}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="text-sm text-muted-foreground px-3 py-2 hover:text-foreground transition-colors"
          >
            Saltar
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => api?.scrollPrev()}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-sm font-medium rounded-full border border-border disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
            >
              Anterior
            </button>
            {isLast ? (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform"
              >
                Empezar mi sesión
              </button>
            ) : (
              <button
                onClick={() => api?.scrollNext()}
                className="px-4 py-2 text-sm font-medium rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform"
              >
                Siguiente
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
